import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, PaperPlaneTilt, Paperclip } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { inr, fmtDateTime, priorityBadge, requestStatusBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const STATUSES = ["Open", "In Progress", "Waiting for Information", "Resolved", "Closed"];
const CLIENT_STATUSES = ["Pending", "In Review", "Approved", "Rejected", "Completed"];
const CLIENT_TYPES = ["Additional AUM Request", "Interest Withdrawal Request", "Emergency Capital Withdrawal Request", "Others"];

export default function AdminRequests() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const navigate = useNavigate();

  const load = () => api.get("/admin/requests").then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, []);

  const [resets, setResets] = useState(null);
  const loadResets = () => api.get("/admin/password-resets").then((r) => setResets(r.data)).catch(() => setResets([]));
  useEffect(() => { loadResets(); }, []);

  const resetPw = async (r) => {
    const pw = window.prompt(`Set a new password for ${r.name} (${r.email}).\nIt must meet the password policy.`);
    if (!pw) return;
    try {
      await api.post(`/admin/users/${r.user_id}/reset-password`, { new_password: pw });
      await api.post(`/admin/password-resets/${r.id}/resolve`);
      toast.success(`Password reset for ${r.name} — share it with them securely.`);
      loadResets();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolveReset = async (r) => {
    try {
      await api.post(`/admin/password-resets/${r.id}/resolve`);
      toast.success("Reset request marked resolved");
      loadResets();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = (r) => { setSel(r); setReply(""); };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/admin/requests/${sel.id}/reply`, { message: reply.trim() });
      toast.success("Reply sent — WRM notified");
      setReply("");
      const r = await api.get("/admin/requests");
      setRows(r.data);
      setSel(r.data.find((x) => x.id === sel.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const update = async (fields) => {
    try {
      await api.patch(`/admin/requests/${sel.id}`, fields);
      toast.success("Request updated — WRM notified");
      const r = await api.get("/admin/requests");
      setRows(r.data);
      setSel(r.data.find((x) => x.id === sel.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadAttachment = async (r) => {
    try {
      const res = await api.get(`/requests/${r.id}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = r.attachment_name || "attachment";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <AppLayout title="Requests"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!rows) return <AppLayout title="Requests"><PageLoader /></AppLayout>;

  const filtered = rows.filter((r) =>
    (filter === "All" || r.status === filter) && (typeFilter === "All" || r.type === typeFilter));
  const openCount = rows.filter((r) => ["Open", "In Progress", "Waiting for Information"].includes(r.status)).length;
  const clientCount = rows.filter((r) => r.source === "investor").length;

  return (
    <AppLayout title="Requests" subtitle={`${openCount} open · ${rows.length} total · ${clientCount} from clients`}>
      <div className="fade-up space-y-5">
        {resets && resets.filter((r) => r.status === "Pending").length > 0 && (
          <div className="glass-card overflow-hidden" data-testid="password-resets-card">
            <div className="px-6 pt-5 pb-4 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Password Reset Requests</h3>
              <Badge variant="outline" className="text-[10px] border-amber-500/25 bg-amber-500/10 text-amber-300">
                {resets.filter((r) => r.status === "Pending").length} pending
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="password-resets-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>User</th>
                    <th className={thCls}>Login ID</th>
                    <th className={thCls}>Role</th>
                    <th className={thCls}>Note</th>
                    <th className={thCls}>Raised</th>
                    <th className={`${thCls} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {resets.filter((r) => r.status === "Pending").map((r) => (
                    <tr key={r.id} className={rowCls} data-testid={`reset-row-${r.id}`}>
                      <td className={tdCls}>
                        <p className="font-medium text-white text-xs">{r.name}</p>
                        <p className="text-[10px] text-zinc-500">{r.email}</p>
                      </td>
                      <td className={`${tdCls} tnum text-xs text-zinc-300`}>{r.username || "—"}</td>
                      <td className={tdCls}>
                        <Badge variant="outline" className="text-[10px] border-white/10 text-zinc-400 uppercase">{r.role}</Badge>
                      </td>
                      <td className={`${tdCls} text-xs text-zinc-400 max-w-[200px] truncate`}>{r.note || "—"}</td>
                      <td className={`${tdCls} tnum text-[11px] text-zinc-500 whitespace-nowrap`}>{fmtDateTime(r.created_at)}</td>
                      <td className={`${tdCls} text-right whitespace-nowrap`}>
                        <button data-testid={`reset-pw-${r.id}`} onClick={() => resetPw(r)}
                          className="text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 mr-3 transition-colors duration-200">
                          Reset Password
                        </button>
                        <button data-testid={`reset-resolve-${r.id}`} onClick={() => resolveReset(r)}
                          className="text-[11px] font-semibold text-zinc-500 hover:text-zinc-300 transition-colors duration-200">
                          Resolve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2" data-testid="request-filters">
          {["All", ...STATUSES, ...CLIENT_STATUSES].map((s) => (
            <button key={s} data-testid={`req-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(s)}
              className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors duration-200 ${
                filter === s ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {s}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2" data-testid="request-type-filters">
          {["All", ...CLIENT_TYPES].map((s) => (
            <button key={s} data-testid={`req-type-filter-${s.toLowerCase().replace(/[\s/]+/g, "-")}`} onClick={() => setTypeFilter(s)}
              className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors duration-200 ${
                typeFilter === s ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {s === "All" ? "All Types" : s}
            </button>
          ))}
        </div>

        <div className="glass-card overflow-hidden">
          {filtered.length === 0 ? <EmptyState message="No requests in this view." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-requests-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>Request ID</th>
                    <th className={thCls}>WRM</th>
                    <th className={thCls}>Client</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Priority</th>
                    <th className={thCls}>Subject</th>
                    <th className={thCls}>Status</th>
                    <th className={thCls}>Raised</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} onClick={() => openDetail(r)} data-testid={`request-row-${r.id}`}
                      className={`${rowCls} cursor-pointer`}>
                      <td className={`${tdCls} tnum text-xs text-emerald-300`}>{r.request_code}</td>
                      <td className={`${tdCls} text-xs text-zinc-200`}>
                        {r.wrm_name || "—"}
                        {r.source === "investor" && <p className="text-[9px] text-sky-300/80 mt-0.5">via client</p>}
                      </td>
                      <td className={`${tdCls} text-xs text-zinc-400`}>{r.client_name || "—"}</td>
                      <td className={`${tdCls} text-xs text-zinc-300`}>{r.type}</td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${priorityBadge(r.priority)}`}>{r.priority}</Badge></td>
                      <td className={`${tdCls} text-xs text-zinc-300 max-w-[220px] truncate`}>{r.subject}</td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${requestStatusBadge(r.status)}`}>{r.status}</Badge></td>
                      <td className={`${tdCls} tnum text-[11px] text-zinc-500 whitespace-nowrap`}>{fmtDateTime(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-white flex items-center gap-3 flex-wrap">
                  {sel.request_code}
                  <Badge variant="outline" className={`text-[10px] ${priorityBadge(sel.priority)}`}>{sel.priority}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${requestStatusBadge(sel.status)}`}>{sel.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div>
                  <p className="label-caps mb-1.5">Subject</p>
                  <p className="text-sm text-white font-medium" data-testid="req-subject">{sel.subject}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {sel.type} · Raised by {sel.source === "investor" ? sel.investor_name : sel.wrm_name} · {fmtDateTime(sel.created_at)}
                  </p>
                  {sel.requested_amount != null && (
                    <p className="text-sm text-emerald-300 tnum mt-2" data-testid="req-amount">Requested Amount: {inr(sel.requested_amount)}</p>
                  )}
                </div>
                <div>
                  <p className="label-caps mb-1.5">Description</p>
                  <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{sel.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sel.client_id && (
                    <Button size="sm" variant="outline" data-testid="req-open-client"
                      onClick={() => navigate(`/admin/clients/${sel.client_id}`)}
                      className="border-white/10 text-zinc-300 hover:bg-white/5">
                      Open Client
                    </Button>
                  )}
                  {sel.has_attachment && (
                    <Button size="sm" variant="outline" data-testid="req-download-attachment"
                      onClick={() => downloadAttachment(sel)}
                      className="border-white/10 text-zinc-300 hover:bg-white/5">
                      <Paperclip size={13} className="mr-1.5" /> {sel.attachment_name || "Attachment"}
                    </Button>
                  )}
                </div>

                <div>
                  <p className="label-caps mb-2">Thread</p>
                  <div className="space-y-2.5" data-testid="req-thread">
                    {(sel.thread || []).length === 0 && <p className="text-xs text-zinc-600">No replies yet.</p>}
                    {(sel.thread || []).map((t) => (
                      <div key={t.id} className="border border-white/[0.06] bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-emerald-300">{t.by} <span className="text-zinc-600 font-normal">({t.role})</span></p>
                          <p className="text-[10px] text-zinc-600 tnum">{fmtDateTime(t.at)}</p>
                        </div>
                        <p className="text-sm text-zinc-300 mt-1.5">{t.message}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} data-testid="req-reply-input"
                      placeholder="Reply to WRM..." className="glass-input flex-1" />
                    <Button onClick={sendReply} data-testid="req-reply-send"
                      className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                      <PaperPlaneTilt size={15} weight="bold" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="label-caps">Status</label>
                    <select value={sel.status} data-testid="req-status-select"
                      onChange={(e) => update({ status: e.target.value })}
                      className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                      {(sel.source === "investor" ? CLIENT_STATUSES : STATUSES).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="label-caps">Internal Note</label>
                    <div className="flex gap-2">
                      <input defaultValue={sel.internal_note || ""} id="req-internal-note" data-testid="req-internal-note"
                        placeholder="Internal only..." className="glass-input flex-1" />
                      <Button size="sm" variant="outline" data-testid="req-note-save"
                        onClick={() => update({ internal_note: document.getElementById("req-internal-note").value })}
                        className="border-white/10 text-zinc-300 hover:bg-white/5 h-10">
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
